import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Avatar,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Schedule,
  DirectionsCar,
} from '@mui/icons-material';

interface TripReadinessItem {
  id: string;
  bookingId: string;
  vehicle: string;
  customer: string;
  scheduledTime: string;
  status: 'ok' | 'needs-attention' | 'at-risk';
  issues: string[];
  location: string;
}

export const PreTripReadiness: React.FC = () => {
  // Mock data for pre-trip readiness
  const upcomingTrips: TripReadinessItem[] = [
    {
      id: '1',
      bookingId: 'BK-8742',
      vehicle: 'Toyota Camry (2023)',
      customer: 'John Smith',
      scheduledTime: '9:00 AM',
      status: 'ok',
      issues: [],
      location: 'Lagos, Nigeria',
    },
    {
      id: '2',
      bookingId: 'BK-8745',
      vehicle: 'BMW 5 Series (2022)',
      customer: 'Sarah Johnson',
      scheduledTime: '1:00 PM',
      status: 'needs-attention',
      issues: ['Missing Insurance Doc'],
      location: 'Nairobi, Kenya',
    },
    {
      id: '3',
      bookingId: 'BK-8748',
      vehicle: 'Range Rover Sport (2023)',
      customer: 'Michael Brown',
      scheduledTime: '8:00 AM',
      status: 'at-risk',
      issues: ['Multiple Docs Missing', 'Driver Not Assigned'],
      location: 'Accra, Ghana',
    },
    {
      id: '4',
      bookingId: 'BK-8743',
      vehicle: 'Audi Q5 (2023)',
      customer: 'Emily Wilson',
      scheduledTime: '10:30 AM',
      status: 'ok',
      issues: [],
      location: 'Lagos, Nigeria',
    },
    {
      id: '5',
      bookingId: 'BK-8746',
      vehicle: 'Tesla Model Y (2023)',
      customer: 'David Clark',
      scheduledTime: '2:15 PM',
      status: 'needs-attention',
      issues: ['Potential Substitution'],
      location: 'Lagos, Nigeria',
    },
  ];

  const statusStats = {
    ok: upcomingTrips.filter(trip => trip.status === 'ok').length,
    needsAttention: upcomingTrips.filter(trip => trip.status === 'needs-attention').length,
    atRisk: upcomingTrips.filter(trip => trip.status === 'at-risk').length,
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ok':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'needs-attention':
        return <Warning sx={{ color: 'warning.main' }} />;
      case 'at-risk':
        return <Error sx={{ color: 'error.main' }} />;
      default:
        return <Schedule sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ok':
        return 'success';
      case 'needs-attention':
        return 'warning';
      case 'at-risk':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ok':
        return 'All Clear';
      case 'needs-attention':
        return 'Needs Attention';
      case 'at-risk':
        return 'Urgent Action';
      default:
        return 'Unknown';
    }
  };

  const readinessPercentage = Math.round((statusStats.ok / upcomingTrips.length) * 100);

  return (
    <Card sx={{ height: 500 }}>
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Pre-Trip Readiness (Next 24hrs)
          </Typography>
          <Chip
            label={`${readinessPercentage}% Ready`}
            color={readinessPercentage >= 80 ? 'success' : readinessPercentage >= 60 ? 'warning' : 'error'}
            variant="outlined"
          />
        </Box>

        {/* Status Summary */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip
              icon={<CheckCircle />}
              label={`OK Status (${statusStats.ok})`}
              color="success"
              variant="outlined"
              size="small"
            />
            <Chip
              icon={<Warning />}
              label={`Needs Attention (${statusStats.needsAttention})`}
              color="warning"
              variant="outlined"
              size="small"
            />
            <Chip
              icon={<Error />}
              label={`At Risk (${statusStats.atRisk})`}
              color="error"
              variant="outlined"
              size="small"
            />
          </Box>
          
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Overall Readiness
            </Typography>
            <LinearProgress
              variant="determinate"
              value={readinessPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'action.selected',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                },
              }}
              color={readinessPercentage >= 80 ? 'success' : readinessPercentage >= 60 ? 'warning' : 'error'}
            />
          </Box>
        </Box>

        {/* Trip List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List disablePadding>
            {upcomingTrips.map((trip, index) => (
              <React.Fragment key={trip.id}>
                <ListItem
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      mr: 2,
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <DirectionsCar sx={{ fontSize: 16 }} />
                  </Avatar>

                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {trip.bookingId} • {trip.scheduledTime}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getStatusIcon(trip.status)}
                          <Chip
                            label={getStatusLabel(trip.status)}
                            size="small"
                            color={getStatusColor(trip.status) as any}
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {trip.vehicle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Customer: {trip.customer} • {trip.location}
                        </Typography>
                        {trip.issues.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {trip.issues.map((issue, issueIndex) => (
                              <Chip
                                key={issueIndex}
                                label={issue}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5, height: 18, fontSize: '0.6875rem' }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < upcomingTrips.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button fullWidth variant="text" size="small">
            View All Upcoming Trips
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};